import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal, ViewContainerRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { NamespaceInterface } from '@vendasta/embeddings';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { AiKnowledgeService } from '../../services/ai-knowledge.service';
import {
  CAN_SHARE_TOKEN,
  EDITING_ENABLED_TOKEN,
  KNOWLEDGE_GRANTS_ENABLED_TOKEN,
} from '../../tokens';
import { KnowledgeBaseDetailSourcesTableComponent } from '../knowledge-base-detail-sources-table/knowledge-base-detail-sources-table.component';
import { KnowledgeBaseGrantSdkService } from '../knowledge-base-grant-sdk.service';
import { KnowledgeBaseSharingDialogComponent } from '../knowledge-base-sharing-dialog/knowledge-base-sharing-dialog.component';
import {
  KnowledgeBaseSourcesDialogComponent,
  KnowledgeBaseSourcesDialogData,
  KnowledgeBaseSourcesResult,
  SourcesDialogKnowledgeSource,
} from '../knowledge-base-sources-dialog/knowledge-base-sources-dialog.component';
import { KnowledgeRowMapper } from '../knowledge-row.mapper';
import { namespacesEqual } from '../namespace.util';
import {
  KnowledgeBaseDetail,
  KnowledgeBaseGrant,
  KnowledgeBaseSharingDialogData,
  TableKnowledgeBase,
  TableKnowledgeSource,
} from '../types';

type LoadState = 'loading' | 'loaded' | 'not-found' | 'error';

/**
 * Routable container for the dedicated Knowledge Collection detail page. Given a `:kbId` route param it
 * gates on the `knowledge_grants_enabled` flag (KD1), fetches and assembles a {@link KnowledgeBaseDetail}
 * with explicit loading / not-found / error states, renders {@link KnowledgeBaseDetailSourcesTableComponent},
 * and wires its outputs to the real grant SDK + KB service with a refetch after every successful mutation.
 *
 * The story demo host (`knowledge-collection.stories.ts`) is reference UX only — its add/remove/describe
 * handlers mutate local state and persist nothing. This container persists through the real RPCs and is the
 * production owner of those actions. Description is read-only this slice (KD7); back navigation uses the
 * platform galaxy back button driven by route-data `previousPageUrl` (KD4).
 */
@Component({
  selector: 'ai-knowledge-knowledge-collection-detail-page',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    GalaxyPageModule,
    GalaxyEmptyStateModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    KnowledgeBaseDetailSourcesTableComponent,
  ],
  templateUrl: './knowledge-collection-detail-page.component.html',
  styleUrls: ['./knowledge-collection-detail-page.component.scss'],
  providers: [KnowledgeRowMapper],
})
export class KnowledgeCollectionDetailPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly grants = inject(KnowledgeBaseGrantSdkService);
  private readonly mapper = inject(KnowledgeRowMapper);
  private readonly dialog = inject(MatDialog);
  // Sharing dialog opens via MatDialog (root overlay); pass this so the dialog's injector chains to this
  // component's (module) injector and can resolve KnowledgeBaseGrantSdkService + the per-app tokens.
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly snackbar = inject(SnackbarService);
  private readonly confirm = inject(OpenConfirmationModalService);
  private readonly translate = inject(TranslateService);
  private readonly canShare$ = inject(CAN_SHARE_TOKEN);
  private readonly editingEnabled$ = inject(EDITING_ENABLED_TOKEN);
  private readonly grantsEnabled$ = inject(KNOWLEDGE_GRANTS_ENABLED_TOKEN);

  readonly loadState = signal<LoadState>('loading');
  /** In-page busy overlay while a mutation + refetch runs, distinct from the full-page initial loading state. */
  readonly isBusy = signal(false);
  readonly detail = signal<KnowledgeBaseDetail | null>(null);
  readonly canShare = signal(false);
  readonly editingEnabled = signal(false);

  /** Deterministic back target (the Collections list); supplied per-app via route `data` (KD2/KD4). */
  previousPageUrl?: string;
  previousPageTitle?: string;

  private kbId = '';
  /** The fetched KB's own namespace — passed to mutation RPCs so they key off the right namespace. */
  private kbNamespace: NamespaceInterface | null = null;
  /** Owned sources the user can add to this collection (caller-namespace sources). */
  private ownedSourceOptions: SourcesDialogKnowledgeSource[] = [];

  async ngOnInit(): Promise<void> {
    this.previousPageUrl = this.route.snapshot.data['previousPageUrl'];
    this.previousPageTitle = this.route.snapshot.data['previousPageTitle'];
    this.kbId = this.route.snapshot.paramMap.get('kbId') ?? '';

    // KD1 self-gate: there is no route-level flag guard today, so the container redirects to the list
    // route when the flag is off rather than rendering a page that shouldn't be reachable.
    const grantsOn = (await firstValueFrom(this.grantsEnabled$).catch(() => false)) ?? false;
    if (!grantsOn) {
      await this.router.navigateByUrl(this.previousPageUrl ?? '/');
      return;
    }

    this.canShare.set((await firstValueFrom(this.canShare$).catch(() => false)) ?? false);
    this.editingEnabled.set((await firstValueFrom(this.editingEnabled$).catch(() => false)) ?? false);

    await this.load(true);
  }

  retry(): void {
    void this.load(true);
  }

  /** Back-to-list action for the not-found state; `navigateByUrl` honors the `?tab=collections` query string. */
  back(): void {
    if (this.previousPageUrl) void this.router.navigateByUrl(this.previousPageUrl);
  }

  /**
   * Fetch the KB, its member sources, and its shares, and assemble the detail.
   * `initial` drives the full-page loading / not-found / error shell; a reload (initial=false) leaves the
   * loaded page in place and reports failure via snackbar so the page never flickers to an error shell.
   */
  private async load(initial: boolean): Promise<boolean> {
    if (initial) this.loadState.set('loading');
    try {
      const callerNs = this.knowledgeService.buildNamespaceInterface();
      const kbs = await this.knowledgeService.listKnowledgeBases(true);
      const kbIface = kbs.find((kb) => kb.id === this.kbId);
      if (!kbIface) {
        this.detail.set(null);
        this.loadState.set('not-found');
        return true;
      }
      const sources = await this.knowledgeService.listAllKnowledgeSources();
      const kbRow = await this.mapper.toKbRow(kbIface, callerNs);
      const memberIds = new Set(kbRow.sourceIds);
      const memberSources = await Promise.all(
        sources
          .filter((s) => !!s.id && memberIds.has(s.id))
          .map((s) => this.mapper.toKsRow(s, callerNs)),
      );
      // The Shared-with card lists caller-issued grants (KD8); skip the fetch entirely when sharing is off.
      const shares = this.canShare() ? await this.grants.listGrantsForKB(this.kbId) : [];

      this.kbNamespace = kbIface.namespace ?? callerNs;
      this.ownedSourceOptions = sources
        .filter((s) => !!s.id && namespacesEqual(s.namespace ?? callerNs, callerNs))
        .map((s) => ({
          id: s.id ?? '',
          kind: this.mapper.ksKindFromConfig(s),
          name: s.name ?? '',
          subtitle: this.knowledgeService.getKnowledgePreview(s),
        }));

      this.detail.set({ kb: kbRow, sources: memberSources, shares });
      this.loadState.set('loaded');
      return true;
    } catch (err) {
      console.error('[knowledge-management] failed to load knowledge collection detail', err);
      if (initial) this.loadState.set('error');
      return false;
    }
  }

  /** Refetch after a mutation, showing the in-page busy overlay rather than the full-page loading shell. */
  private async reload(): Promise<boolean> {
    this.isBusy.set(true);
    try {
      return await this.load(false);
    } finally {
      this.isBusy.set(false);
    }
  }

  onShare(detail: KnowledgeBaseDetail): void {
    const data: KnowledgeBaseSharingDialogData = {
      kbId: detail.kb.id,
      kbName: detail.kb.name,
      // Add-only: the Shared-with card already lists current shares with inline revoke.
      hideCurrentShares: true,
    };
    this.dialog
      .open(KnowledgeBaseSharingDialogComponent, { data, width: '720px', maxWidth: '92vw', autoFocus: false, viewContainerRef: this.viewContainerRef })
      .afterClosed()
      .subscribe(() => void this.reload());
  }

  onAddSource(detail: KnowledgeBaseDetail): void {
    const kbNamespace = this.kbNamespace ?? this.knowledgeService.buildNamespaceInterface();
    const data: KnowledgeBaseSourcesDialogData = {
      kbId: detail.kb.id,
      kbName: detail.kb.name,
      kbDescription: detail.kb.description,
      managedBy: undefined,
      currentSources: detail.sources.map((s) => ({ id: s.id, kind: s.kind, name: s.name, subtitle: s.subtitle })),
      availableSources: this.ownedSourceOptions,
      // The merged dialog awaits onApply and only closes on success — surfacing errors and keeping the
      // selection on failure (mirrors KnowledgeBaseTableComponent.openSourcesDialog).
      onApply: async (result: KnowledgeBaseSourcesResult) => {
        await this.knowledgeService.updateKnowledgeBaseSources(detail.kb.id, kbNamespace, result.sourceIds);
      },
    };
    this.dialog
      .open(KnowledgeBaseSourcesDialogComponent, { data, width: '640px', maxWidth: '92vw', autoFocus: false })
      .afterClosed()
      .subscribe((result?: KnowledgeBaseSourcesResult) => {
        if (result) void this.reload();
      });
  }

  /**
   * Per-row "Remove from collection". Confirms first, then detaches the membership (the source itself is
   * untouched, so it stays on the Sources tab). Confirmed update model: no optimistic removal — refetch on
   * success, error snackbar on failure with the row left in place.
   */
  async onRemoveSource({ kb, source }: { kb: TableKnowledgeBase; source: TableKnowledgeSource }): Promise<void> {
    const confirmed = await firstValueFrom(
      this.confirm.openModal({
        type: 'warn',
        title: this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REMOVE_SOURCE_TITLE'),
        message: this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REMOVE_SOURCE_MESSAGE', {
          source: source.name,
          kb: kb.name,
        }),
        confirmButtonText: this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REMOVE_SOURCE_CONFIRM'),
      }),
    );
    if (!confirmed) return;

    const remaining = (this.detail()?.kb.sourceIds ?? []).filter((id) => id !== source.id);
    const kbNamespace = this.kbNamespace ?? this.knowledgeService.buildNamespaceInterface();
    this.isBusy.set(true);
    try {
      await this.knowledgeService.updateKnowledgeBaseSources(kb.id, kbNamespace, remaining);
      await this.load(false);
      this.snackbar.openSuccessSnack(
        this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REMOVE_SOURCE_SUCCESS', { source: source.name, kb: kb.name }),
      );
    } catch (err) {
      console.error('[knowledge-management] removeSource failed', err);
      this.snackbar.openErrorSnack(this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REMOVE_SOURCE_ERROR'));
    } finally {
      this.isBusy.set(false);
    }
  }

  /**
   * Inline revoke from the Shared-with card. On `{ ok: false, dependents }` the grant is still active and
   * cleanup needs a follow-up action that isn't available yet — surface an honest blocked message rather
   * than implying auto-cleanup. On success, refetch the shares.
   */
  async onRevokeShare(grant: KnowledgeBaseGrant): Promise<void> {
    this.isBusy.set(true);
    try {
      const result = await this.grants.revokeGrant(this.kbId, grant.grantId);
      if (!('dependents' in result)) {
        await this.load(false);
        return;
      }
      this.snackbar.openErrorSnack(
        this.translate.instant('KNOWLEDGE_SHARING.DETAIL.REVOKE_BLOCKED', { target: grant.target.displayName }),
      );
    } catch (err) {
      console.error('[knowledge-management] revokeShare failed', err);
      this.snackbar.openErrorSnack(this.translate.instant('KNOWLEDGE_SHARING.SHARE.REVOKE_ERROR'));
    } finally {
      this.isBusy.set(false);
    }
  }
}
